import { applyI18n, t } from '../i18n';
import { pushAppUrl } from '../app/navigationState';
import { iconImg } from './icons';
import { openFilesContextMenu, type FilesContextMenuItem } from './filesContextMenu';

export const SECTION_NAV_MOBILE_MQ = '(max-width: 768px)';

export type SectionNavItem = {
  id: string;
  labelKey: string;
  panel: HTMLElement;
};

export type SectionNavController = {
  setActive(id: string): void;
  setVisible(id: string, visible: boolean): void;
  /** Sync the active panel from `readId` when URL sync is enabled. */
  applyFromUrl(): void;
  getActiveId(): string;
};

export type SectionNavOptions = {
  initialId?: string;
  navAriaLabelKey?: string;
  /** When set, sidebar clicks push this URL and `applyFromUrl` reads the active section from it. */
  buildUrl?: (id: string) => string;
  readId?: () => string | null;
};

export function mountSectionNav(
  page: HTMLElement,
  items: SectionNavItem[],
  options?: SectionNavOptions,
): SectionNavController {
  const layout = document.createElement('div');
  layout.className = 'page-sidebar-layout';

  const switcher = document.createElement('div');
  switcher.className = 'page-panel-switcher';

  const sidebar = document.createElement('aside');
  sidebar.className = 'page-sidebar';

  const nav = document.createElement('nav');
  nav.className = 'page-sidebar-nav';
  if (options?.navAriaLabelKey) {
    nav.setAttribute('data-i18n-aria-label', options.navAriaLabelKey);
  }

  const main = document.createElement('div');
  main.className = 'page-sidebar-main';
  while (page.firstChild) {
    main.append(page.firstChild);
  }

  sidebar.append(nav);
  layout.append(switcher, sidebar, main);
  page.append(layout);

  const available = new Map<string, boolean>();
  for (const item of items) {
    available.set(item.id, !item.panel.hidden);
  }

  const urlPreferred = options?.readId?.() ?? undefined;
  let activeId = resolveActiveId(
    items,
    available,
    urlPreferred && available.get(urlPreferred) ? urlPreferred : options?.initialId,
  );
  const buttons = new Map<string, HTMLButtonElement>();

  function isMobileLayout(): boolean {
    return typeof window !== 'undefined' && window.matchMedia(SECTION_NAV_MOBILE_MQ).matches;
  }

  function syncLayoutMode(): void {
    const mobile = isMobileLayout();
    layout.classList.toggle('page-sidebar-layout--switcher', mobile);
    sidebar.hidden = mobile;
    sidebar.setAttribute('aria-hidden', mobile ? 'true' : 'false');
    switcher.hidden = !mobile;
  }

  function renderSwitcher(): void {
    const active = items.find((item) => item.id === activeId);
    const labelKey = active?.labelKey ?? items[0]?.labelKey ?? '';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'page-panel-switcher__control sedr-room-chrome__panel sedr-room-chrome__panel--menu';
    button.setAttribute('aria-haspopup', 'menu');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('data-i18n-aria-label', 'nav.switchSection');
    button.setAttribute('data-i18n-title', 'nav.switchSection');
    button.setAttribute('aria-label', t('nav.switchSection'));
    button.title = t('nav.switchSection');

    const text = document.createElement('span');
    text.setAttribute('data-i18n', labelKey);
    text.textContent = t(labelKey);
    button.append(text, iconImg('chevron-down', 14));

    button.addEventListener('click', () => {
      button.setAttribute('aria-expanded', 'true');
      const menuItems: FilesContextMenuItem[] = items
        .filter((item) => available.get(item.id))
        .map((item) => ({
          id: item.id,
          labelKey: item.labelKey,
          action: () => navigateToSection(item.id),
        }));
      openFilesContextMenu({
        anchorRect: button.getBoundingClientRect(),
        items: menuItems,
      });
      const observer = new MutationObserver(() => {
        if (!document.querySelector('[data-files-context-menu="true"]')) {
          button.setAttribute('aria-expanded', 'false');
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    });

    applyI18n(button);
    switcher.replaceChildren(button);
  }

  function renderButtons(): void {
    nav.replaceChildren();
    buttons.clear();
    for (const item of items) {
      if (!available.get(item.id)) {
        continue;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = `page-sidebar-link${item.id === activeId ? ' page-sidebar-link--active' : ''}`;
      button.dataset.section = item.id;
      button.setAttribute('data-i18n', item.labelKey);
      button.addEventListener('click', () => {
        navigateToSection(item.id);
      });
      nav.append(button);
      buttons.set(item.id, button);
    }
    applyI18n(nav);
  }

  function applyPanels(): void {
    for (const item of items) {
      item.panel.hidden = item.id !== activeId || !available.get(item.id);
    }
  }

  function setActive(id: string): void {
    if (!available.get(id)) {
      return;
    }

    activeId = id;
    for (const [itemId, button] of buttons) {
      button.classList.toggle('page-sidebar-link--active', itemId === id);
    }
    applyPanels();
    renderSwitcher();
  }

  function navigateToSection(id: string, navOptions?: { replace?: boolean }): void {
    if (!available.get(id)) {
      return;
    }

    if (options?.buildUrl) {
      const url = options.buildUrl(id);
      const current = `${window.location.pathname}${window.location.search}`;
      if (url !== current) {
        pushAppUrl(url, { replace: navOptions?.replace });
      }
    }

    setActive(id);
  }

  function setVisible(id: string, visible: boolean): void {
    if (!items.some((item) => item.id === id)) {
      return;
    }

    available.set(id, visible);
    if (!visible && activeId === id) {
      activeId = resolveActiveId(items, available);
    }
    renderButtons();
    applyPanels();
    renderSwitcher();
  }

  function applyFromUrl(): void {
    if (!options?.readId) {
      return;
    }

    const fromUrl = options.readId();
    if (fromUrl && available.get(fromUrl)) {
      if (fromUrl !== activeId) {
        setActive(fromUrl);
      }
      return;
    }

    // Unavailable explicit section (e.g. Auditor on /admin/settings): fall back and fix URL.
    if (fromUrl && !available.get(fromUrl)) {
      const fallback = resolveActiveId(items, available);
      if (fallback) {
        navigateToSection(fallback, { replace: true });
      }
      return;
    }

    // Bare `/admin` (null): show the default first available section without rewriting the URL.
    const fallback = resolveActiveId(items, available);
    if (fallback && fallback !== activeId) {
      setActive(fallback);
    }
  }

  renderButtons();
  applyPanels();
  renderSwitcher();
  syncLayoutMode();

  const mediaQuery = window.matchMedia(SECTION_NAV_MOBILE_MQ);
  const onMediaChange = () => {
    if (!page.isConnected) {
      return;
    }
    syncLayoutMode();
  };
  mediaQuery.addEventListener('change', onMediaChange);

  const disconnectObserver = new MutationObserver(() => {
    if (!page.isConnected) {
      mediaQuery.removeEventListener('change', onMediaChange);
      disconnectObserver.disconnect();
      return;
    }
    // Page can be mounted after mountSectionNav returns; re-sync once connected.
    syncLayoutMode();
  });
  disconnectObserver.observe(document.body, { childList: true, subtree: true });

  return {
    setActive,
    setVisible,
    applyFromUrl,
    getActiveId: () => activeId,
  };
}

function resolveActiveId(
  items: SectionNavItem[],
  available: Map<string, boolean>,
  preferredId?: string,
): string {
  if (preferredId && available.get(preferredId)) {
    return preferredId;
  }

  return items.find((item) => available.get(item.id))?.id ?? items[0]?.id ?? '';
}
